import addCalendarEventsAndNotify from '../../../store/apis/addCalendarEventsAndNotify';
import CalendarItemNotificationPayloadExtended from '../../store/CalendarItemNotificationPayloadExtended';
import DateRange from '../../../store/schema/DateRange';
import deleteCalendarEventsByInstanceKeysAndNotify from '../../../store/apis/deleteCalendarEventsByInstanceKeysAndNotify';
import ExpandCalendarEventResponse from 'owa-service/lib/contract/ExpandCalendarEventResponse';
import expandCalendarEventService from '../../../services/expandCalendarEventService';
import getDateRange from '../../../store/apis/getDateRange';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import reloadCalendarEventsAndNotify from '../../../store/apis/reloadCalendarEventsAndNotify';
import { areDateRangesEqual } from 'mini-datetime-utils';
import { CalendarEvent } from 'mini-calendar-event-types';
import { calendarEvent } from 'mini-calendar-event-converter';
import { convertDateRangeToDisplayDateRange } from '../../../utils/epochTimeToDisplayDate';
import { trace } from 'owa-trace';

/**
 * Pending recurrence expansions map with key = recurring item instance key and value = the action that was called.
 */
const pendingRecurrenceExpansionRequests: {
    [instanceKey: string]: Promise<ItemInfoResponseMessage> | Promise<ExpandCalendarEventResponse>;
} = {};

/**
 * Handles a notification for a recurring item
 */
export default function handleRecurringItemNotification(
    folderId: string,
    notification: CalendarItemNotificationPayloadExtended
) {
    const calEvent: CalendarEvent = calendarEvent(notification.NotificationPayload.Item);
    if (calEvent.UID) {
        const cacheRange = getDateRange();
        const transformedCacheRange = convertDateRangeToDisplayDateRange(cacheRange);
        const getExpandedItemActionV2 = expandCalendarEventService(
            calEvent.ItemId,
            transformedCacheRange
        );
        pendingRecurrenceExpansionRequests[calEvent.InstanceKey] = getExpandedItemActionV2;

        getExpandedItemActionV2
            .then((response: ExpandCalendarEventResponse) => {
                if (response && response.ResponseClass === 'Success') {
                    onSuccessfulExpandedRecurrence(
                        calEvent,
                        getExpandedItemActionV2,
                        cacheRange,
                        folderId,
                        // tslint:disable-next-line: no-unnecessary-callback-wrapper
                        response.Occurrences.map(item => calendarEvent(item)),
                        pendingRecurrenceExpansionRequests,
                        notification.InstanceKeyToDelete
                    );
                } else {
                    onFailedExpandedRecurrence(
                        calEvent,
                        getExpandedItemActionV2,
                        folderId,
                        response,
                        notification.InstanceKeyToDelete
                    );
                }
            })
            .catch((error: Error) => {
                onFailedExpandedRecurrence(
                    calEvent,
                    getExpandedItemActionV2,
                    folderId,
                    null /* response */,
                    notification.InstanceKeyToDelete,
                    error.message
                );
            });
    }
}

/**
 * This is run when we succeed to get the expanded recurring series for a notification item
 */
function onSuccessfulExpandedRecurrence(
    notificationItem: CalendarEvent,
    getExpandedItemAction: Promise<ItemInfoResponseMessage> | Promise<ExpandCalendarEventResponse>,
    requestedRange: DateRange,
    folderId: string,
    expandedItems: CalendarEvent[],
    pendingRecurrenceExpansionRequests: {
        [instanceKey: string]:
            | Promise<ItemInfoResponseMessage>
            | Promise<ExpandCalendarEventResponse>;
    },
    instanceKeyToDelete: string
) {
    let seriesEvents = expandedItems;

    if (
        shouldProcessRecurringSeriesAction(
            notificationItem,
            getExpandedItemAction,
            pendingRecurrenceExpansionRequests
        )
    ) {
        // Process expanded items
        trace.info(
            `[CalendarNotifications] Action to expand series completed successfully. Retrieved ${
                seriesEvents ? seriesEvents.length : 0
            } items.`
        );

        // If the range in the cache is different from what we originally requested, we may have received less items than
        // we are currently showing. Therefore, if we simply added what we got back from the action, we may be missing items.
        // In this case we'll reload to be on the safe side.
        const cacheDateRange = getDateRange();
        if (
            !areDateRangesEqual(
                convertDateRangeToDisplayDateRange(requestedRange),
                convertDateRangeToDisplayDateRange(cacheDateRange)
            )
        ) {
            reloadCalendarEventsAndNotify(folderId);
        } else {
            if (seriesEvents.length > 0) {
                seriesEvents = seriesEvents.filter(e => e.ResponseType !== 'Decline');
            }
            // Remove all the items related to the series and replace them with the newly expanded items.
            deleteCalendarEventsByInstanceKeysAndNotify([
                notificationItem.InstanceKey,
                instanceKeyToDelete,
            ]);
            addCalendarEventsAndNotify(seriesEvents);
        }

        // Remove from pending operations map
        delete pendingRecurrenceExpansionRequests[notificationItem.InstanceKey];
    }
}

/**
 * Callback for when we fail to get the expanded recurring series for a notification item
 * @param notificationItem The notification item from which we expanded
 * @param getExpandedItemAction The action that was invoked to expand the item's recurring series
 * @param folderId Calendar folderId of the notification item
 * @param response The response we got from the service call (if any)
 * @param exceptionMessage The exception message (if any)
 */
function onFailedExpandedRecurrence(
    notificationItem: CalendarEvent,
    getExpandedItemAction: Promise<ExpandCalendarEventResponse> | Promise<ItemInfoResponseMessage>,
    folderId: string,
    response: ExpandCalendarEventResponse | ItemInfoResponseMessage,
    instanceKeyToDelete: string,
    exceptionMessage: string = null
) {
    if (
        shouldProcessRecurringSeriesAction(
            notificationItem,
            getExpandedItemAction,
            pendingRecurrenceExpansionRequests
        )
    ) {
        if (response && response.ResponseCode === 'ErrorItemNotFound') {
            trace.warn(`[CalendarNotifications] Action to expand recurring series failed with item not found because item is not on the server. Removing series.
                Item Instance Key: ${notificationItem.InstanceKey}`);
            deleteCalendarEventsByInstanceKeysAndNotify([
                notificationItem.InstanceKey,
                instanceKeyToDelete,
            ]);
        } else {
            // If we don't know what went wrong, we'll reload to make sure that we don't have stale data
            const errorMessage = response
                ? `${response.ResponseCode}: ${response.MessageText}`
                : exceptionMessage;
            trace.warn(`[CalendarNotifications] Action to expand recurring series failed for unknown reason, reloading cache.
                Item Instance Key: ${notificationItem.InstanceKey},
                Message: '${errorMessage}'`);
            reloadCalendarEventsAndNotify(folderId);
        }

        // Remove from pending operations map
        delete pendingRecurrenceExpansionRequests[notificationItem.InstanceKey];
    }
}

/**
 * Checks whether the results of the specified action to get an expanded recurring series should be processed
 * @param notificationItem The notification item
 * @param getExpandedItemAction The action that was invoked to expand the item's recurring series
 * @param pendingRecurrenceExpansionRequests Pending recurrence expansions map with key = recurring item instance key and value = the action that was called.
 * @returns True if we should process the specified action to expand recurring items
 */
function shouldProcessRecurringSeriesAction(
    notificationItem: CalendarEvent,
    getExpandedItemAction: Promise<ExpandCalendarEventResponse> | Promise<ItemInfoResponseMessage>,
    pendingRecurrenceExpansionRequests: {
        [instanceKey: string]:
            | Promise<ItemInfoResponseMessage>
            | Promise<ExpandCalendarEventResponse>;
    }
): boolean {
    // Only process action if we have it registered as a pending action,
    // and it is the latest action we issued for the particular series
    const pendingAction = pendingRecurrenceExpansionRequests[notificationItem.InstanceKey];
    return !!pendingAction && pendingAction === getExpandedItemAction;
}
